import { Controller } from "@hotwired/stimulus"

// Live preview for the Zuschlagsprofil matrix editor.
//
// Display only — Costing::SurchargeMatrix stays the authority for what a position is
// actually priced with. This just saves a round trip while the user types.
//
// "Einfach" mode keeps storing a full matrix: the primary column's values are mirrored
// into the hidden ones, so the calculation engine never needs a single-surcharge branch.
export default class extends Controller {
  static targets = [
    "cell",
    "total",
    "example",
    "mode",
    "simpleToggle",
    "column",
    "columnLabel",
    "simpleHint",
    "preset",
    "presetStatus",
  ]
  static values = { primary: String, simpleLabel: String, presets: Object }

  connect() {
    this.originalLabels = new Map()
    this.columnLabelTargets.forEach((label) => this.originalLabels.set(label, label.textContent))
    this.recalculate()
  }

  toggleSimple() {
    const simple = this.simpleToggleTarget.checked

    // Switching to "einfach" overwrites the hidden columns with the primary one, so stash
    // the differentiated values first — toggling the checkbox by accident must not destroy
    // a matrix the user spent time on. Restoring skips the primary column: edits made
    // there while simple mode was on must survive switching back.
    if (simple) {
      this.differentiatedValues = this.cellTargets.map((cell) => cell.value)
    } else if (this.differentiatedValues) {
      this.cellTargets.forEach((cell, index) => {
        if (cell.dataset.costType === this.primaryValue) return
        cell.value = this.differentiatedValues[index]
      })
    }

    this.columnTargets.forEach((column) => {
      if (column.dataset.costType !== this.primaryValue) {
        column.hidden = simple
      }
    })

    this.columnLabelTargets.forEach((label) => {
      if (label.dataset.costType !== this.primaryValue) return
      label.textContent = simple ? this.simpleLabelValue : this.originalLabels.get(label)
    })

    if (this.hasSimpleHintTarget) this.simpleHintTarget.hidden = !simple

    this.recalculate()
  }

  // "Vorlage anwenden": overwrites the inputs with the chosen preset (Costing::SurchargePresets).
  // Nothing is saved here — the user still has to press Speichern.
  applyPreset() {
    const preset = this.hasPresetTarget ? this.presetsValue[this.presetTarget.value] : null
    if (!preset) return

    // A preset that differs per Kostenart cannot live in "einfach" mode: the mirroring would
    // flatten it onto the primary column. Leave simple mode first, dropping the stash so the
    // old differentiated values are not restored over the preset.
    if (this.simpleMode && !this.uniform(preset.rates)) {
      this.differentiatedValues = null
      this.simpleToggleTarget.checked = false
      this.toggleSimple()
    }

    this.cellTargets.forEach((cell) => {
      const value = preset.rates[cell.dataset.costType]?.[cell.dataset.layer]
      if (value !== undefined) cell.value = value
    })

    if (preset.mode && this.hasModeTarget) this.writeMode(preset.mode)
    if (this.hasPresetStatusTarget) this.presetStatusTarget.hidden = false

    this.recalculate()
  }

  uniform(rates) {
    const columns = Object.values(rates).map((layers) => JSON.stringify(layers))
    return columns.every((column) => column === columns[0])
  }

  // The mode select is SlimSelect-backed; writing the native value would be reverted by its
  // store, so go through the select controller when it is there.
  writeMode(mode) {
    if (this.modeTarget.value === mode) return

    const select = this.application.getControllerForElementAndIdentifier(this.modeTarget, "select")
    if (select) {
      select.setSelected(mode)
    } else {
      this.modeTarget.value = mode
    }
  }

  recalculate() {
    if (this.simpleMode) this.mirrorPrimaryColumn()

    this.totalTargets.forEach((cell) => {
      const percent = (this.factorFor(cell.dataset.costType) - 1) * 100
      cell.innerHTML = `+${this.formatNumber(percent)}&nbsp;%`
    })

    if (this.hasExampleTarget) {
      const result = 100 * this.factorFor(this.primaryValue)
      this.exampleTarget.textContent = `${this.formatNumber(result)} €`
    }
  }

  // Copies the primary Kostenart's percentages into every hidden column so a saved
  // "einfach" profile is a complete, uniform matrix.
  mirrorPrimaryColumn() {
    const source = {}
    this.cellTargets.forEach((cell) => {
      if (cell.dataset.costType === this.primaryValue) source[cell.dataset.layer] = cell.value
    })

    this.cellTargets.forEach((cell) => {
      if (cell.dataset.costType === this.primaryValue) return
      if (source[cell.dataset.layer] !== undefined) cell.value = source[cell.dataset.layer]
    })
  }

  factorFor(costType) {
    const layers = {}
    this.cellTargets.forEach((cell) => {
      if (cell.dataset.costType === costType) layers[cell.dataset.layer] = this.percent(cell.value)
    })

    const share = (percent) => 1 + percent / 100

    if (this.cascading) {
      return (
        share(layers.bgk) * share(layers.agk) * share((layers.risk || 0) + (layers.profit || 0))
      )
    }

    return share((layers.bgk || 0) + (layers.agk || 0) + (layers.risk || 0) + (layers.profit || 0))
  }

  get cascading() {
    return !this.hasModeTarget || this.modeTarget.value === "cascading"
  }

  get simpleMode() {
    return this.hasSimpleToggleTarget && this.simpleToggleTarget.checked
  }

  percent(value) {
    const parsed = parseFloat(String(value).replace(",", "."))
    return Number.isFinite(parsed) ? parsed : 0
  }

  formatNumber(value) {
    return value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}
